import React from 'react';

import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import { alpha } from '@mui/material/styles';

import moment from 'moment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';

import RequestManager from '../../utilities/requestManager';
import { StateManager } from '../../utilities/stateManager';
import {
  StackedDialog, useSelectMenuProps, useDatePickerSlotProps,
} from '../../utilities/ModalStackContext';

const BLUE = '#023e84';

// Which statement the affidavit swears to. These keys must match ITEMS in the
// backend (skyway-functions dmv/affidavitForm.js) — the server refuses a key it
// does not know.
//
// Only "Other" takes a statement, and it is the one every affidavit already on
// file uses. The other three are complete sentences on the form with no blanks
// of their own, so ticking the box is the whole job. The items that DO have
// their own blanks are left off deliberately: guessing which blank takes which
// value is how a sworn statement ends up saying something nobody meant.
const ITEMS = [
  { value: 'other', label: 'Other — explain in your own words', needsStatement: true },
  { value: 'purchaser_in_error', label: 'My name was entered in error as purchaser' },
  { value: 'lienholder_in_error', label: 'My name was entered in error as lien-holder' },
  { value: 'not_operated', label: 'The vehicle has not been and will not be operated on Florida highways' },
];

// Every affidavit already on file says a version of one of these, so offer them
// rather than making the clerk type it again.
const PRESETS = [
  { short: 'Wrong line', text: 'Seller signed on the wrong line in error. Crossed out and corrected. No fraud intended.' },
  { short: 'Signed twice', text: 'Seller accidentally signed the same name on the second line. Crossed out and corrected. No fraud intended.' },
];

// The statement prints on three lines of the form, roughly this many characters
// in total. Past it the tail would not fit, so the server reports it rather than
// trimming a sworn statement.
const STATEMENT_LIMIT = 330;

// Fill in an affidavit and put it in the packet.
//
// It is never signed here: the form has no signature field at all, and the
// affiant signs in ink after printing. So this collects what has to be TYPED and
// nothing else.
export default function AffidavitDialog({ open, onClose, car = {}, onCreated }) {
  const menuProps = useSelectMenuProps();
  const dateSlots = useDatePickerSlotProps();

  const [item, setItem] = React.useState('other');
  const [statement, setStatement] = React.useState('');
  const [affiant, setAffiant] = React.useState('');
  const [date, setDate] = React.useState(moment());
  const [busy, setBusy] = React.useState(false);

  // On a consignment the affiant is usually the consignor — theirs is the
  // signature that needed explaining.
  const consignor = car.consignor || {};
  const consignorName = typeof consignor === 'object'
    ? (`${consignor.first_name || ''} ${consignor.last_name || ''}`.trim() || consignor.company_name || '')
    : '';

  // Reset every time it opens, so one car's wording is never sworn to on
  // another by accident.
  React.useEffect(() => {
    if (!open) return;
    setItem('other');
    setStatement('');
    setAffiant('');
    setDate(moment());
    setBusy(false);
  }, [open]);

  const chosen = ITEMS.find(i => i.value === item) || ITEMS[0];
  const needsStatement = !!chosen.needsStatement;
  const tooLong = statement.length > STATEMENT_LIMIT;
  const canCreate = !busy && !tooLong && (!needsStatement || !!statement.trim());

  const create = async () => {
    setBusy(true);
    const res = await RequestManager.post({
      function: 'generateDmvDocument',
      variables: {
        stockNumber: car.stock,
        doctype: 'affidavit',
        asExtra: true,
        formData: {
          item,
          statement: statement.trim(),
          affiant: affiant.trim() || consignorName,
          date: date && date.isValid() ? date.format('MM/DD/YYYY') : '',
        },
      },
    });
    setBusy(false);

    if (!res || res.success === false || res.ok === false) {
      return StateManager.setAlertAndOpen(
        (res && (res.message || res.error)) || 'Could not make the affidavit.', 'error');
    }
    onCreated?.(res);
    onClose();
  };

  const vehicle = [car.year, car.make, car.model].filter(Boolean).join(' ');

  return (
    <StackedDialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pb: 0.5 }}>Affidavit</DialogTitle>
      <DialogContent>
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 2 }}>
          It prints unsigned — the affiant signs in ink. The vehicle details below come
          off the car and are filled in for you.
        </Typography>

        <Box sx={{ p: 1.25, mb: 2, borderRadius: 1, bgcolor: alpha(BLUE, 0.04) }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{vehicle || car.stock}</Typography>
          <Stack direction="row" sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
            <Chip size="small" label={`VIN ${car.vin || '—'}`} sx={{ height: 20, fontSize: '0.7rem' }} />
            <Chip size="small" label={`Title ${car.title_number || '—'}`} sx={{ height: 20, fontSize: '0.7rem' }} />
          </Stack>
        </Box>

        <Stack spacing={2}>
          <FormControl size="small" fullWidth>
            <InputLabel>What it swears to</InputLabel>
            <Select
              label="What it swears to"
              MenuProps={menuProps}
              value={item}
              onChange={(e) => setItem(e.target.value)}
            >
              {ITEMS.map(i => (
                <MenuItem key={i.value} value={i.value}>{i.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {needsStatement && (
            <Box>
              <TextField
                label="Statement"
                size="small"
                fullWidth
                multiline
                minRows={3}
                value={statement}
                onChange={(e) => setStatement(e.target.value)}
                placeholder="What happened, in the affiant's own words."
                error={tooLong}
                helperText={tooLong
                  ? `${statement.length} characters — too long for the three lines on the form`
                  : `${statement.length} of about ${STATEMENT_LIMIT} characters`}
              />
              <Stack direction="row" sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
                {PRESETS.map(p => (
                  <Button key={p.short} size="small" onClick={() => setStatement(p.text)}
                    sx={{ fontSize: '0.7rem', textTransform: 'none' }}>
                    {p.short}
                  </Button>
                ))}
              </Stack>
            </Box>
          )}

          <TextField
            label="Affiant — the printed name under the signature"
            size="small"
            fullWidth
            value={affiant}
            onChange={(e) => setAffiant(e.target.value)}
            placeholder={consignorName || 'Who is swearing it'}
            helperText={affiant.trim() || !consignorName ? ' ' : `Blank uses ${consignorName}`}
          />

          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DatePicker
              label="Date"
              value={date}
              onChange={setDate}
              slotProps={{
                textField: { size: 'small', fullWidth: true },
                popper: dateSlots.popper,
                dialog: dateSlots.dialog,
              }}
            />
          </LocalizationProvider>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 2 }}>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!canCreate}
          onClick={create}
          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : null}
          sx={{ bgcolor: BLUE, '&:hover': { bgcolor: alpha(BLUE, 0.85) } }}
        >
          {busy ? 'Making…' : 'Create and add to packet'}
        </Button>
      </DialogActions>
    </StackedDialog>
  );
}
